import { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';

import { APIValidationError, ValidationErrors } from '../types';
import { ProblemDetails } from '../../gamma-api';

export const DEFAULT_ERROR_NAME = 'Error';
export const SERVER_ERROR_MESSAGE = 'Внутренняя ошибка сервера';
export const NETWORK_ERROR_MESSAGE = 'Network Error';
const IDENTITY_ERROR_CODES = {
  DuplicateUserName: 'identity_DuplicateUserName',
};

export const isNetworkError = (error?: Error): boolean => {
  return error?.message === NETWORK_ERROR_MESSAGE;
};

export const isAxiosError = (error?: unknown): boolean => {
  if (!error) {
    return false;
  }

  const axiosError = error as AxiosError;
  return axiosError.isAxiosError;
};

export const isUnprocessableEntityError = (error: Error): boolean => {
  const axiosError = error as AxiosError;
  return axiosError.isAxiosError && axiosError.response?.status === StatusCodes.UNPROCESSABLE_ENTITY;
};

export const isInvalidCastError = (error: Error): boolean => {
  const axiosError = error as AxiosError;
  return axiosError.response?.data?.title === 'InvalidCast';
};

export const tryGetValidationErrors = (error: Error): ValidationErrors | undefined => {
  const axiosError = error as AxiosError;

  if (axiosError.isAxiosError && axiosError.response?.status === StatusCodes.UNPROCESSABLE_ENTITY) {
    const details = (axiosError.response.data as { detail: APIValidationError[] }).detail;
    const fieldErrors = details?.reduce<ValidationErrors>((acc, cur) => {
      // NOTE: loc array represents the chain of fields in nested objects, we take only the innermost field name.
      const fieldName = cur?.loc.at(-1);
      if (fieldName) {
        acc[fieldName] = { type: cur.type };
      }
      return acc;
    }, {});
    return fieldErrors;
  }

  return undefined;
};

const tryExtractErrorsFirstMessage = (errorData?: { [key: string]: string }) => {
  let errors = Object.values(errorData?.errors || {});

  if (!errors.length) {
    errors = Object.keys(errorData || {})
      .map((key) => (key.startsWith('identity_') ? errorData?.[key] : ''))
      .filter((str) => !!str)
      .map((str) => str as string);
  }

  return errors.length ? errors[0]?.toString() : errorData;
};

export const getErrorMessage = (error: Error): string => {
  let message = '';

  const axiosError = error as AxiosError;
  if (axiosError.isAxiosError && axiosError.response) {
    message = isUnprocessableEntityError(error)
      ? tryExtractErrorsFirstMessage(axiosError.response.data)
      : axiosError.response.status <= StatusCodes.INTERNAL_SERVER_ERROR
      ? axiosError.response.data.detail || axiosError.response.data || SERVER_ERROR_MESSAGE
      : SERVER_ERROR_MESSAGE;
  }

  return message || error.message || String(error);
};

export const hasDuplicateUserName = (error: Error): boolean => {
  const axiosError = error as AxiosError;
  return (
    axiosError.isAxiosError &&
    isUnprocessableEntityError(error) &&
    axiosError.response?.data[IDENTITY_ERROR_CODES.DuplicateUserName]
  );
};

export const getProblemDetails = (error: unknown, instance: string = window.location.pathname): ProblemDetails => {
  let type = 'about:blank';
  let title = 'Internal Server Error';
  let detail = 'An unexpected error occurred.';
  let extensions: Record<string, unknown> = {};

  const axiosError = error as AxiosError;
  if (axiosError.isAxiosError && axiosError.response) {
    const data = axiosError.response.data;

    type = data.type || axiosError.code || type;
    title = data.title || axiosError.name || title;
    detail = axiosError.message || detail;
    instance = data.instance || instance;
    extensions = data.extensions || extensions;

    if (typeof data.detail === 'string') {
      detail = data.detail || detail;
    }

    if (!extensions.traceback) {
      extensions.traceback = JSON.stringify(data.detail, undefined, 2);
    }
  } else if (error instanceof Error) {
    title = error.name || title;
    detail = error.message || detail;

    // Keep stack trace (string) as an extension
    if (error.stack) {
      extensions.traceback = error.stack;
    }
  } else if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;

    // Helper: safely convert unknown to string
    const toStr = (value: unknown): string | undefined =>
      typeof value === 'string' ? value : value != null ? String(value) : undefined;

    type = toStr(errObj.type) || type;
    title = toStr(errObj.title) || title;
    detail = toStr(errObj.detail || errObj.message) || detail;
    instance = toStr(errObj.instance) || instance;

    for (const [key, value] of Object.entries(errObj)) {
      if (!['type', 'title', 'detail', 'message', 'instance'].includes(key)) {
        extensions[key] = value;
      }
    }
  } else if (typeof error === 'string') {
    detail = error;
  }

  return {
    type,
    title,
    detail,
    instance,
    extensions,
  };
};
